import React, { useContext, useEffect, useState } from "react";
import "react-quill/dist/quill.snow.css";
import { Search_Slugs } from "../../api/generated/graphql";
import { StorageContext } from "../../api/storage/provider";
import {
  FAQSection,
  FeaturesSection,
  HeroSection,
  MetaInfoSection,
  PropertiesSection,
} from "./sections";

interface PropertyGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  addPropertyGroup: (propertyGroup: Data) => void | Promise<void>;
  editPropertyGroup?: (dirtyFieldValues: Data) => void | Promise<void>;
  editData?: Search_Slugs | null;
  isEditMode?: boolean;
  editDataLoading?: boolean;
}

export interface PropertyGroupApiData {
  id?: string;
  sessionId?: string | null;
  nlqId?: string | null;
  title: string | null;
  query: string | null;
  language_id: number | null;
  slug: string;
  metadata: {
    meta_title: string;
    meta_description: string;
    keywords: string[];
  };
  heroSection: {
    header: string;
    heroImage: string;
    mainParagraph: string;
  };
  style: {
    properties_section: {
      title: string;
      description: string;
    };
    features_section: {
      title: string;
      description: string;
    };
  };
  questions: {
    title: string;
    description: string;
    items: Array<{
      question: string;
      answer: string;
    }>;
  };
}

export interface Data {
  id?: string;
  sessionId?: string | null;
  nlqId?: string | null;
  title: string | null;
  description: string | null;
  slug: string | null;
  query: string | null;
  imageUrl: string | null;
  language_id: number | null;
  metadata?: {
    meta_title: string;
    meta_description: string;
    keywords: string[];
  };
  header_text?: string | null;
  heroImage?: string | null;
  mainParagraph?: string | null;
  style?: {
    properties_section: {
      title: string;
      description: string;
    };
    features_section: {
      title: string;
      description: string;
    };
  };
  questions?: {
    title: string;
    description: string;
    items: Array<{
      question: string;
      answer: string;
    }>;
  };
}

export default function PropertyGroupModal({
  isOpen,
  onClose,
  addPropertyGroup,
  editPropertyGroup,
  editData,
  isEditMode = false,
  editDataLoading = false,
}: PropertyGroupModalProps) {
  const [formData, setFormData] = useState<PropertyGroupApiData>({
    slug: "",
    title: null,
    query: null,
    language_id: null,
    metadata: {
      meta_title: "",
      meta_description: "",
      keywords: ["", "", "", "", ""],
    },
    heroSection: {
      header: "",
      heroImage: "",
      mainParagraph: "",
    },
    style: {
      properties_section: {
        title: "",
        description: "",
      },
      features_section: {
        title: "",
        description: "",
      },
    },
    questions: {
      title: "",
      description: "",
      items: Array(5)
        .fill(null)
        .map(() => ({
          question: "",
          answer: "",
        })),
    },
  });

  const [expandedSections, setExpandedSections] = useState<string[]>([]);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingHeroImage, setIsUploadingHeroImage] = useState(false);
  const storage = useContext(StorageContext);

  // Quill editor configuration
  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      [
        {
          color: [
            "#000000",
            "#4a4a4a",
            "#666666",
            "#999999",
            "#e60000",
            "#ff9900",
            "#ffff00",
            "#008a00",
            "#0066cc",
            "#9933ff",
            "#ffffff",
            "#facccc",
            "#ffebcc",
            "#ffffcc",
            "#cce8cc",
            "#cce0f5",
            "#ebd6ff",
            "#bbbbbb",
            "#f06666",
            "#ffc266",
            "#ffff66",
            "#66b966",
            "#66a3e0",
            "#c285ff",
          ],
        },
      ],
      ["link"],
      ["clean"],
    ],
  };

  const quillFormats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "list",
    "bullet",
    "color",
    "background",
    "link",
  ];

  // Initialize form data from database entry or create new entry
  useEffect(() => {
    if (isOpen) {
      setSaveError(null);
      setExpandedSections(["metaSection", "heroSection"]);

      if (!isEditMode) {
        // Initialize with empty data for create mode
        setFormData({
          slug: "",
          title: null,
          query: null,
          language_id: null,
          metadata: {
            meta_title: "",
            meta_description: "",
            keywords: ["", "", "", "", ""],
          },
          heroSection: {
            header: "",
            heroImage: "",
            mainParagraph: "",
          },
          style: {
            properties_section: {
              title: "",
              description: "",
            },
            features_section: {
              title: "",
              description: "",
            },
          },
          questions: {
            title: "",
            description: "",
            items: Array(5)
              .fill(null)
              .map(() => ({
                question: "",
                answer: "",
              })),
          },
        });
        return;
      }

      // In edit mode, wait for data to be loaded
      if (editDataLoading) {
        // Don't populate form while loading
        return;
      }

      if (!editData) {
        // Data not loaded yet, wait
        return;
      }

      // Parse JSON fields safely - handle both string and object formats
      let metadata: any = {};
      if (editData.metadata) {
        if (typeof editData.metadata === "string") {
          try {
            metadata = JSON.parse(editData.metadata);
          } catch (e) {
            console.error("Error parsing metadata:", e);
            metadata = {};
          }
        } else {
          metadata = editData.metadata;
        }
      }

      let style: any = {};
      if (editData.style) {
        if (typeof editData.style === "string") {
          try {
            style = JSON.parse(editData.style);
          } catch (e) {
            console.error("Error parsing style:", e);
            style = {};
          }
        } else {
          style = editData.style;
        }
      }

      let questions: any = {};
      if (editData.questions) {
        if (typeof editData.questions === "string") {
          try {
            questions = JSON.parse(editData.questions);
          } catch (e) {
            console.error("Error parsing questions:", e);
            questions = {};
          }
        } else {
          questions = editData.questions;
        }
      }

      // Handle both camelCase (new format) and snake_case (old format) for backward compatibility
      const metaTitle = metadata.metaTitle || metadata.meta_title || "";
      const metaDescription = metadata.metaDescription || metadata.meta_description || "";
      const keywordsArray = Array.isArray(metadata.keywords) ? metadata.keywords : [];
      
      // Ensure keywords array has exactly 5 slots
      const normalizedKeywords = Array(5)
        .fill("")
        .map((_, index) => (keywordsArray[index] || ""));

      // Ensure FAQ items array has exactly 5 slots
      const faqItemsArray = Array.isArray(questions.items) ? questions.items : [];
      const normalizedFAQItems = Array(5)
        .fill(null)
        .map((_, index) => {
          const existingItem = faqItemsArray[index];
          return existingItem && typeof existingItem === "object"
            ? {
                question: existingItem.question || "",
                answer: existingItem.answer || "",
              }
            : { question: "", answer: "" };
        });

      // Ensure style sections have proper structure
      const propertiesSection = style.properties_section || {};
      const featuresSection = style.features_section || {};

      setFormData({
        id: editData.id,
        sessionId: editData.session_id || null,
        nlqId: editData.search_session?.natural_language_queries?.[0]?.id || null,
        title: editData.search_session?.title || null,
        query: editData.search_session?.natural_language_queries?.[0]?.query || null,
        language_id: editData.language_id || null,
        slug: editData.slug || "",
        metadata: {
          meta_title: metaTitle,
          meta_description: metaDescription,
          keywords: normalizedKeywords,
        },
        heroSection: {
          header: editData.header_text || "",
          heroImage: editData.image || "",
          mainParagraph: editData.description || "",
        },
        style: {
          properties_section: {
            title: propertiesSection.title || "",
            description: propertiesSection.description || "",
          },
          features_section: {
            title: featuresSection.title || "",
            description: featuresSection.description || "",
          },
        },
        questions: {
          title: questions.title || "",
          description: questions.description || "",
          items: normalizedFAQItems,
        },
      });
    }
  }, [editData, isOpen, isEditMode, editDataLoading]);

  const toggleSection = (sectionName: string) => {
    setExpandedSections((prev) =>
      prev.includes(sectionName)
        ? prev.filter((name) => name !== sectionName)
        : [...prev, sectionName]
    );
  };

  const handleMetadataChange = (
    field: keyof PropertyGroupApiData["metadata"],
    value: string
  ) => {
    setFormData((prev) => ({
      ...prev,
      metadata: {
        ...prev.metadata,
        [field]: value,
      },
    }));
  };

  const handleKeywordChange = (index: number, value: string) => {
    setFormData((prev) => ({
      ...prev,
      metadata: {
        ...prev.metadata,
        keywords: prev.metadata.keywords.map((keyword: string, i: number) =>
          i === index ? value : keyword
        ),
      },
    }));
  };

  const handleSlugChange = (slug: string) => {
    setFormData((prev) => ({
      ...prev,
      slug,
    }));
  };

  const handleQueryChange = (query: string) => {
    setFormData((prev) => ({
      ...prev,
      query: query || null,
    }));
  };

  const handleLanguageChange = (language_id: number) => {
    setFormData((prev) => ({
      ...prev,
      language_id: language_id || null,
    }));
  };

  const handleHeroChange = (field: "header" | "mainParagraph", value: string) => {
    setFormData((prev) => ({
      ...prev,
      heroSection: {
        ...prev.heroSection,
        [field]: value,
      },
    }));
  };

  const handlePropertiesSectionChange = (
    field: "title" | "description",
    value: string
  ) => {
    setFormData((prev) => ({
      ...prev,
      style: {
        ...prev.style,
        properties_section: {
          ...prev.style.properties_section,
          [field]: value,
        },
      },
    }));
  };

  const handleFeaturesSectionChange = (
    field: "title" | "description",
    value: string
  ) => {
    setFormData((prev) => ({
      ...prev,
      style: {
        ...prev.style,
        features_section: {
          ...prev.style.features_section,
          [field]: value,
        },
      },
    }));
  };

  const handleFAQSectionChange = (field: "title" | "description", value: string) => {
    setFormData((prev) => ({
      ...prev,
      questions: {
        ...prev.questions,
        [field]: value,
      },
    }));
  };

  const handleFAQItemChange = (
    index: number,
    field: "question" | "answer",
    value: string
  ) => {
    setFormData((prev) => ({
      ...prev,
      questions: {
        ...prev.questions,
        items: prev.questions.items.map((item, i) =>
          i === index ? { ...item, [field]: value } : item
        ),
      },
    }));
  };

  const handleHeroImageRemove = () => {
    setFormData((prev) => ({
      ...prev,
      heroSection: {
        ...prev.heroSection,
        heroImage: "",
      },
    }));
  };

  const handleHeroImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      setIsUploadingHeroImage(true);
      setSaveError(null);

      try {
        if (!storage) {
          throw new Error("Storage client not initialized");
        }

        if (!process.env.REACT_APP_STORAGE_URL) {
          throw new Error(
            "REACT_APP_STORAGE_URL environment variable not configured"
          );
        }

        const timestamp = Date.now();
        const filename = `property-group-hero-${timestamp}-${file.name.replace(
          /[^a-zA-Z0-9.-]/g,
          "_"
        )}`;

    const { error, fileMetadata } = await storage.upload({
          file: new File([file], filename, { type: file.type }),
          bucketId: "property-images",
    });
    
    if (!error && fileMetadata) {
      const imageUrl = `${process.env.REACT_APP_STORAGE_URL}/files/${fileMetadata.id}`;

          setFormData((prev) => ({
            ...prev,
            heroSection: {
              ...prev.heroSection,
              heroImage: imageUrl,
            },
          }));
    } else {
          console.error("Error uploading hero image:", error);
          const errorMessage = error?.message || "Unknown upload error";
          setSaveError(`Failed to upload image: ${errorMessage}`);
        }
      } catch (err) {
        console.error("Unexpected error uploading hero image:", err);
        const errorMessage =
          err instanceof Error ? err.message : "Unknown error";
        setSaveError(`Failed to upload image: ${errorMessage}`);
      } finally {
        setIsUploadingHeroImage(false);
      }
    } else if (event.target.value === "") {
      // Handle image removal
      setFormData((prev) => ({
        ...prev,
        heroSection: {
          ...prev.heroSection,
          heroImage: "",
        },
      }));
    }
  };

  const handleSave = async () => {
    try {
      setSaveError(null);
      setIsSaving(true);

      // Validation
      if (!formData.metadata.meta_title.trim()) {
        setSaveError("Meta Title is required");
        return;
      }
      if (!formData.metadata.meta_description.trim()) {
        setSaveError("Meta Description is required");
        return;
      }
      if (!formData.slug.trim()) {
        setSaveError("Slug is required");
        return;
      }
      if (!formData.heroSection.header.trim()) {
        setSaveError("Header (H1) is required");
        return;
      }
      if (!formData.heroSection.mainParagraph.trim()) {
        setSaveError("Main Paragraph is required");
        return;
      }
      if (!formData.query) {
        setSaveError("Query is required");
        return;
      }
      if (!formData.language_id) {
        setSaveError("Language is required");
        return;
      }

      const dataToSave: Data = {
        id: formData.id,
        sessionId: formData.sessionId,
        nlqId: formData.nlqId,
        title: formData.heroSection.header, // Use header as title
        description: formData.heroSection.mainParagraph,
        slug: formData.slug,
        query: formData.query,
        imageUrl: formData.heroSection.heroImage,
        language_id: formData.language_id,
        metadata: {
          meta_title: formData.metadata.meta_title,
          meta_description: formData.metadata.meta_description,
          keywords: formData.metadata.keywords.filter((k) => k.trim() !== ""),
        },
        header_text: formData.heroSection.header,
        heroImage: formData.heroSection.heroImage,
        mainParagraph: formData.heroSection.mainParagraph,
        style: {
          properties_section: {
            title: formData.style.properties_section.title,
            description: formData.style.properties_section.description,
          },
          features_section: {
            title: formData.style.features_section.title,
            description: formData.style.features_section.description,
          },
        },
        questions: {
          title: formData.questions.title,
          description: formData.questions.description,
          items: formData.questions.items.filter(
            (faq) => faq.question.trim() !== "" || faq.answer.trim() !== ""
          ),
        },
      };

      if (isEditMode && editPropertyGroup) {
        await editPropertyGroup(dataToSave);
      } else {
        await addPropertyGroup(dataToSave);
      }

      onClose();
    } catch (error) {
      console.error("Error saving property group:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Failed to save property group";
      setSaveError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  // Show loading state when fetching edit data
  if (isEditMode && editDataLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-6xl w-full h-[95vh] overflow-y-auto">
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Loading Property Group Data
              </h3>
              <p className="text-gray-600 text-center max-w-md">
                Please wait while we fetch the property group information...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-6xl w-full h-[95vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b p-6 z-10">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-800">
              {isEditMode
                ? "Edit Property Group"
                : "Create New Property Group"}
            </h2>
          <button
            onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-2xl font-bold">
              ×
          </button>
          </div>
        </div>

        <div className="p-6 space-y-8">
          {/* Meta Information Section */}
          <div className="bg-blue-50 p-6 rounded-lg border">
            <button
              type="button"
              onClick={() => toggleSection("metaSection")}
              className="w-full flex items-center justify-between text-left focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-lg p-2">
              <h3 className="text-lg font-semibold text-gray-800">
                Meta Information
              </h3>
              <svg
                className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${
                  expandedSections.includes("metaSection") ? "rotate-180" : ""
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            {expandedSections.includes("metaSection") && (
              <MetaInfoSection
                formData={formData}
                onMetadataChange={handleMetadataChange}
                onKeywordChange={handleKeywordChange}
                onSlugChange={handleSlugChange}
              />
            )}
                  </div>

          {/* Hero Section */}
          <div className="bg-green-50 p-6 rounded-lg border">
            <button
              type="button"
              onClick={() => toggleSection("heroSection")}
              className="w-full flex items-center justify-between text-left focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-lg p-2">
              <h3 className="text-lg font-semibold text-gray-800">
                Hero Section
              </h3>
              <svg
                className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${
                  expandedSections.includes("heroSection") ? "rotate-180" : ""
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            {expandedSections.includes("heroSection") && (
              <HeroSection
                formData={formData}
                onHeroChange={handleHeroChange}
                onQueryChange={handleQueryChange}
                onLanguageChange={handleLanguageChange}
                onImageUpload={handleHeroImageUpload}
                onImageRemove={handleHeroImageRemove}
                isUploadingImage={isUploadingHeroImage}
                quillModules={quillModules}
                quillFormats={quillFormats}
              />
            )}
                </div>

          {/* Properties Section */}
          <div className="bg-yellow-50 p-6 rounded-lg border">
            <button
              type="button"
              onClick={() => toggleSection("propertiesSection")}
              className="w-full flex items-center justify-between text-left focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-lg p-2">
              <h3 className="text-lg font-semibold text-gray-800">
                Properties Section
              </h3>
              <svg
                className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${
                  expandedSections.includes("propertiesSection")
                    ? "rotate-180"
                    : ""
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            {expandedSections.includes("propertiesSection") && (
              <PropertiesSection
                formData={formData}
                onSectionChange={handlePropertiesSectionChange}
                quillModules={quillModules}
                quillFormats={quillFormats}
              />
            )}
              </div>

          {/* Features Section */}
          <div className="bg-purple-50 p-6 rounded-lg border">
            <button
              type="button"
              onClick={() => toggleSection("featuresSection")}
              className="w-full flex items-center justify-between text-left focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-lg p-2">
              <h3 className="text-lg font-semibold text-gray-800">
                Features Section
              </h3>
              <svg
                className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${
                  expandedSections.includes("featuresSection")
                    ? "rotate-180"
                    : ""
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            {expandedSections.includes("featuresSection") && (
              <FeaturesSection
                formData={formData}
                onSectionChange={handleFeaturesSectionChange}
                quillModules={quillModules}
                quillFormats={quillFormats}
              />
            )}
                  </div>

          {/* FAQ Section */}
          <div className="bg-orange-50 p-6 rounded-lg border">
            <button
              type="button"
              onClick={() => toggleSection("faqSection")}
              className="w-full flex items-center justify-between text-left focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-lg p-2">
              <h3 className="text-lg font-semibold text-gray-800">
                FAQ Section
              </h3>
              <svg
                className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${
                  expandedSections.includes("faqSection") ? "rotate-180" : ""
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            {expandedSections.includes("faqSection") && (
              <FAQSection
                formData={formData}
                onSectionChange={handleFAQSectionChange}
                onFAQItemChange={handleFAQItemChange}
                quillModules={quillModules}
                quillFormats={quillFormats}
              />
            )}
                        </div>
                      </div>

        {/* Modal Footer */}
        <div className="sticky bottom-0 bg-gray-50 px-6 py-4 border-t">
          {saveError && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-red-400"
                    viewBox="0 0 20 20"
                    fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                    </div>
                <div className="ml-3">
                  <p className="text-sm">Error: {saveError}</p>
                </div>
              </div>
            </div>
          )}
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500">
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-70 disabled:cursor-not-allowed">
              {isSaving
                ? isEditMode
                  ? "Updating..."
                  : "Creating..."
                : isEditMode
                  ? "Update Property Group"
                  : "Create Property Group"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
